import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { Coins, Sparkles, CheckCircle2, ArrowRight, Loader2, PartyPopper } from "lucide-react";
import {
  CREDIT_PACKS,
  PRICE_PER_CREDIT_ZAR,
  ADMIN_FEE_ZAR,
  MIN_TOPUP_TOTAL_ZAR,
  PREMIUM_CV_CREDIT_COST,
} from "@/lib/credit-packs";
import { orderCreditPack } from "@/lib/api/credits.functions";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { PayFastBuyNowCredits } from "@/components/site/PayFastBuyNowCredits";

export const Route = createFileRoute("/credits")({
  validateSearch: z.object({ welcome: z.string().optional() }),
  head: () => ({
    meta: [
      { title: "Buy AI Credits — Generate Contracts, CVs & Legal Research | Nkadimeng Danny" },
      { name: "description", content: `Top up AI credits to personalise premium contracts, run legal research, draft documents and chat with the LLB tutor. Instant top-up via PayFast — minimum R${MIN_TOPUP_TOTAL_ZAR}.` },
      { property: "og:title", content: "AI Credits for Premium Legal Tools" },
      { property: "og:description", content: `Instant top-up via PayFast. Minimum R${MIN_TOPUP_TOTAL_ZAR}.` },
    ],
  }),
  component: CreditsPage,
});

function CreditsPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [busy, setBusy] = useState<string | null>(null);
  const orderPack = useServerFn(orderCreditPack);
  const isWelcome = search.welcome === "1";

  async function pickPack(code: string) {
    setBusy(code);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.info("Please sign in or create an account first.");
        navigate({ to: "/auth", search: { redirect: "/credits" } as any });
        return;
      }
      const r = await orderPack({ data: { packCode: code } });
      toast.success("Redirecting to PayFast…");
      const { launchPayfastCheckout } = await import("@/lib/payfast-launch");
      await launchPayfastCheckout(r.reference);
    } catch (e: any) {
      toast.error("Could not create order", { description: e?.message ?? String(e) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="bg-background">
      <section className="bg-gradient-to-b from-navy/5 to-transparent py-12">
        <div className="container-narrow text-center">
          {isWelcome ? (
            <div className="mx-auto mb-6 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-2 text-sm text-navy">
              <PartyPopper className="h-4 w-4 text-gold" />
              Welcome! Pick a pack to instantly add credits via PayFast.
            </div>
          ) : null}
          <Sparkles className="mx-auto h-8 w-8 text-gold" />
          <h1 className="mt-3 font-display text-3xl text-navy md:text-4xl">AI Credits</h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground md:text-base">
            Personalise premium contracts, run legal research, draft documents
            and chat with our LLB tutor. 1 credit = 1 generation.
            <br className="hidden md:block" />
            <strong>You get 10 free credits</strong> the moment you sign up.
            <br className="hidden md:block" />
            <span className="text-xs">
              Pricing: <strong>R{PRICE_PER_CREDIT_ZAR.toFixed(2)}/credit</strong> · admin fee <strong>R{ADMIN_FEE_ZAR}</strong> per top-up · <strong>minimum R{MIN_TOPUP_TOTAL_ZAR} top-up</strong> · premium AI CV = {PREMIUM_CV_CREDIT_COST} credits.
            </span>
          </p>
        </div>
      </section>

      <section className="py-10">
        <div className="container-narrow">
          <div className="grid gap-5 md:grid-cols-4">
            {CREDIT_PACKS.map((p) => {
              return (
                <div key={p.code} className="flex flex-col rounded-2xl border border-border bg-card p-6 shadow-soft transition hover:border-gold">
                  <div className="flex items-center gap-2">
                    <Coins className="h-5 w-5 text-gold" />
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">{p.label}</p>
                  </div>
                  <p className="mt-2 font-display text-3xl text-navy">{p.credits}</p>
                  <p className="text-xs text-muted-foreground">credits</p>
                  <p className="mt-4 text-2xl font-bold text-navy">R{p.amountZar.toFixed(2)}</p>
                  <p className="text-[11px] text-muted-foreground">
                    R{p.creditsValueZar.toFixed(2)} credits + R{p.adminFeeZar.toFixed(2)} admin fee
                  </p>
                  <Button onClick={() => pickPack(p.code)} disabled={busy === p.code} className="mt-5 bg-navy hover:bg-navy/90">
                    {busy === p.code ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Pay with PayFast <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>

          <div className="mt-8 grid gap-4 rounded-2xl border border-border bg-secondary/30 p-6 md:grid-cols-3">
            <div className="flex gap-3"><CheckCircle2 className="h-5 w-5 flex-shrink-0 text-emerald-600" /><div><p className="font-semibold text-navy">Instant PayFast checkout</p><p className="text-xs text-muted-foreground">Pay by card, Instant EFT, or wallet — no waiting for verification.</p></div></div>
            <div className="flex gap-3"><CheckCircle2 className="h-5 w-5 flex-shrink-0 text-emerald-600" /><div><p className="font-semibold text-navy">Credits land automatically</p><p className="text-xs text-muted-foreground">Once PayFast confirms, your balance updates in seconds.</p></div></div>
            <div className="flex gap-3"><CheckCircle2 className="h-5 w-5 flex-shrink-0 text-emerald-600" /><div><p className="font-semibold text-navy">Minimum R{MIN_TOPUP_TOTAL_ZAR}</p><p className="text-xs text-muted-foreground">Includes the R{ADMIN_FEE_ZAR} admin fee — top up more anytime.</p></div></div>
          </div>

          <PayFastBuyNowCredits />

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Already have credits? <Link to={"/portal/credits" as any} className="font-semibold text-navy underline">View balance</Link>
          </p>
        </div>
      </section>
    </div>
  );
}
